/* eslint-disable */

// ⭐️ Code Referred from https://www.npmjs.com/package/@ebay/nice-modal-react ⭐️ 

import React, {
  useEffect, useCallback, useContext, useReducer, useMemo, Suspense,
} from 'react';

const symModalId = Symbol('NiceModalId');
const initialState = {};
export const NiceModalContext = React.createContext(initialState);
const NiceModalIdContext = React.createContext(null);

const MODAL_REGISTRY = {};
const ALREADY_MOUNTED = {};

let uidSeed = 0;
let dispatch = () => {
  throw new Error('No dispatch method detected, did you embed your app with NiceModal.Provider?');
};
const getUid = () => `_nice_modal_${uidSeed += 1}`;

// Modal reducer used in useReducer hook.
export const reducer = (state, action) => {
  switch (action.type) {
    case 'nice-modal/show': {
      const { modalId, args } = action.payload;
      return {
        ...state,
        [modalId]: {
          ...state[modalId],
          id: modalId,
          args,
          visible: !!ALREADY_MOUNTED[modalId],
          delayVisible: !ALREADY_MOUNTED[modalId],
        },
      };
    }
    case 'nice-modal/hide': {
      const { modalId } = action.payload;
      if (!state[modalId]) return state;
      return {
        ...state,
        [modalId]: {
          ...state[modalId],
          visible: false,
        },
      };
    }
    case 'nice-modal/remove': {
      const { modalId } = action.payload;
      const newState = { ...state };
      delete newState[modalId];
      return newState;
    }
    case 'nice-modal/set-flags': {
      const { modalId, flags } = action.payload;
      return {
        ...state,
        [modalId]: {
          ...state[modalId],
          ...flags,
        },
      };
    }
    default:
      return state;
  }
};

// Get modal component by modal id
function getModal(modalId) {
  return MODAL_REGISTRY[modalId]?.comp;
}

// action creator to show a modal
function showModal(modalId, args) {
  return {
    type: 'nice-modal/show',
    payload: {
      modalId,
      args,
    },
  };
}

// action creator to hide a modal
function hideModal(modalId) {
  return {
    type: 'nice-modal/hide',
    payload: {
      modalId,
    },
  };
}

// action creator to set flags of a modal
function setModalFlags(modalId, flags) {
  return {
    type: 'nice-modal/set-flags',
    payload: {
      modalId,
      flags,
    },
  };
}

// action creator to remove a modal
function removeModal(modalId) {
  return {
    type: 'nice-modal/remove',
    payload: {
      modalId,
    },
  };
}

const modalCallbacks = {};
const hideModalCallbacks = {};

const getModalId = (modal) => {
  if (typeof modal === 'string') return modal;
  if (!modal[symModalId]) {
    modal[symModalId] = getUid();
  }
  return modal[symModalId];
};

export function show(modal, args) {
  const modalId = getModalId(modal);
  if (typeof modal !== 'string' && !MODAL_REGISTRY[modalId]) {
    register(modalId, modal);
  }

  dispatch(showModal(modalId, args));
  if (!modalCallbacks[modalId]) {
    let theResolve;
    let theReject;
    const promise = new Promise((resolve, reject) => {
      theResolve = resolve;
      theReject = reject;
    });
    modalCallbacks[modalId] = {
      resolve: theResolve,
      reject: theReject,
      promise,
    };
  }
  return modalCallbacks[modalId].promise;
}

export function hide(modal) {
  const modalId = getModalId(modal);
  dispatch(hideModal(modalId));
  delete modalCallbacks[modalId];
  if (!hideModalCallbacks[modalId]) {
    let theResolve;
    let theReject;
    const promise = new Promise((resolve, reject) => {
      theResolve = resolve;
      theReject = reject;
    });
    hideModalCallbacks[modalId] = {
      resolve: theResolve,
      reject: theReject,
      promise,
    };
  }
  return hideModalCallbacks[modalId].promise;
}

export const remove = (modal) => {
  const modalId = getModalId(modal);
  dispatch(removeModal(modalId));
  delete modalCallbacks[modalId];
  delete hideModalCallbacks[modalId];
};

const setFlags = (modalId, flags) => {
  dispatch(setModalFlags(modalId, flags));
};

export function useModal(modal, args) {
  const modals = useContext(NiceModalContext);
  const contextModalId = useContext(NiceModalIdContext);
  let modalId = null;
  const isUseComponent = modal && typeof modal !== 'string';
  if (!modal) {
    modalId = contextModalId;
  } else {
    modalId = getModalId(modal);
  }

  // Only if contextModalId doesn't exist
  if (!modalId) throw new Error('No modal id found in NiceModal.useModal.');

  const mid = modalId;
  // If use a component directly, register it.
  useEffect(() => {
    if (isUseComponent && !MODAL_REGISTRY[mid]) {
      register(mid, modal, args);
    }
  }, [isUseComponent, mid, modal, args]);

  const modalInfo = modals[mid];

  const showCallback = useCallback((args) => show(mid, args), [mid]);
  const hideCallback = useCallback(() => hide(mid), [mid]);
  const removeCallback = useCallback(() => remove(mid), [mid]);
  const resolveCallback = useCallback(
    (args) => {
      modalCallbacks[mid]?.resolve(args);
      delete modalCallbacks[mid];
    },
    [mid],
  );
  const rejectCallback = useCallback(
    (args) => {
      modalCallbacks[mid]?.reject(args);
      delete modalCallbacks[mid];
    },
    [mid],
  );
  const resolveHide = useCallback(
    (args) => {
      hideModalCallbacks[mid]?.resolve(args);
      delete hideModalCallbacks[mid];
    },
    [mid],
  );

  return useMemo(
    () => ({
      id: mid,
      args: modalInfo?.args,
      visible: !!modalInfo?.visible,
      keepMounted: !!modalInfo?.keepMounted,
      show: showCallback,
      hide: hideCallback,
      remove: removeCallback,
      resolve: resolveCallback,
      reject: rejectCallback,
      resolveHide,
    }),
    [
      mid,
      modalInfo?.args,
      modalInfo?.visible,
      modalInfo?.keepMounted,
      showCallback,
      hideCallback,
      removeCallback,
      resolveCallback,
      rejectCallback,
      resolveHide,
    ],
  );
}

export const create = (Comp) => ({
  defaultVisible, keepMounted, id, ...props
}) => {
  const { args, show } = useModal(id);

  // If there's modal state, then should mount it.
  const modals = useContext(NiceModalContext);
  const shouldMount = !!modals[id];

  useEffect(() => {
    // If defaultVisible, show it after mounted.
    if (defaultVisible) {
      show();
    }

    ALREADY_MOUNTED[id] = true;

    return () => {
      delete ALREADY_MOUNTED[id];
    };
  }, [id, show, defaultVisible]);

  useEffect(() => {
    if (keepMounted) setFlags(id, { keepMounted: true });
  }, [id, keepMounted]);

  const delayVisible = modals[id]?.delayVisible;
  // If modal.show is called
  //  1. If modal was mounted, should make it visible directly
  //  2. If modal has not been mounted, should mount it first, then make it visible
  useEffect(() => {
    if (delayVisible) {
      // delayVisible: false => true, it means the modal.show() is called, should show it.
      show(args);
    }
  }, [delayVisible, args, show]);

  if (!shouldMount) return null;
  return (
    <NiceModalIdContext.Provider value={id}>
      <Comp {...(props)} {...args} />
    </NiceModalIdContext.Provider>
  );
};

// All registered modals will be rendered in modal placeholder
export const register = (id, comp, props) => {
  if (!MODAL_REGISTRY[id]) {
    MODAL_REGISTRY[id] = { comp, props };
  } else {
    MODAL_REGISTRY[id].props = props;
  }
};

export const unregister = (id) => {
  delete MODAL_REGISTRY[id];
};

// The placeholder component is used to auto render modals when call modal.show()
// When modal.show() is called, it means there've been modal info
const NiceModalPlaceholder = () => {
  const modals = useContext(NiceModalContext);
  const visibleModalIds = Object.keys(modals).filter((id) => !!modals[id]);
  visibleModalIds.forEach((id) => {
    if (!MODAL_REGISTRY[id] && !ALREADY_MOUNTED[id]) {
      console.warn(
        `No modal found for id: ${id}. Please check the id or if it is registered or declared via JSX.`,
      );
    }
  });

  const toRender = visibleModalIds
    .filter((id) => MODAL_REGISTRY[id])
    .map((id) => ({
      id,
      ...MODAL_REGISTRY[id],
    }));

  // Modals may be registered as React.lazy components (code-split per surface), so their
  // chunk can still be loading when show() runs. fallback={null} means "render nothing until
  // the modal chunk arrives" — the modal appears a beat later without a page-wide spinner.
  return (
    <Suspense fallback={null}>
      {toRender.map((t) => (
        <t.comp key={t.id} id={t.id} {...t.props} />
      ))}
    </Suspense>
  );
};

const InnerContextProvider = ({ children }) => {
  const arr = useReducer(reducer, initialState);
  const modals = arr[0];
  dispatch = arr[1];
  return (
    <NiceModalContext.Provider value={modals}>
      {children}
      <NiceModalPlaceholder />
    </NiceModalContext.Provider>
  );
};

export const Provider = ({
  children,
  dispatch: givenDispatch,
  modals: givenModals,
}) => {
  if (!givenDispatch || !givenModals) {
    return <InnerContextProvider>{children}</InnerContextProvider>;
  }
  dispatch = givenDispatch;
  return (
    <NiceModalContext.Provider value={givenModals}>
      {children}
      <NiceModalPlaceholder />
    </NiceModalContext.Provider>
  );
};

export const ModalDef = ({
  id,
  component,
}) => {
  useEffect(() => {
    register(id, component);
    return () => {
      unregister(id);
    };
  }, [id, component]);
  return null;
};

export const ModalHolder = ({
  modal,
  handler = {},
  ...restProps
}) => {
  const mid = useMemo(() => getUid(), []);
  const ModalComp = typeof modal === 'string' ? MODAL_REGISTRY[modal]?.comp : modal;

  if (!handler) {
    throw new Error('No handler found in NiceModal.ModalHolder.');
  }
  if (!ModalComp) {
    throw new Error(`No modal found for id: ${modal} in NiceModal.ModalHolder.`);
  }
  handler.show = useCallback((args) => show(mid, args), [mid]);
  handler.hide = useCallback(() => hide(mid), [mid]);

  return <ModalComp id={mid} {...restProps} />;
};

const ModalManager = {
  Provider,
  ModalDef,
  ModalHolder,
  NiceModalContext,
  create,
  register,
  getModal,
  show,
  hide,
  remove,
  useModal,
  reducer,
};

export default ModalManager;
